import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, Target, Heart, MessageCircle, PenTool, Link, AlertTriangle } from 'lucide-react';
import { fetchPassage } from '@/utils/bible/esvApi';
import { stripCodeFences } from '@/utils/guidance/guidanceParser';

interface AbielGuidance {
  mode: 'guide';
  summary: string;
  reading_goals: string[];
  how_to_pray: {
    outline: {
      address: string;
      praise: string;
      confession?: string;
      request: string;
      submission: string;
      thanksgiving: string;
    };
    bullet_points: string[];
  };
  prompts: {
    en: string[];
    zh?: string[];
  };
  journaling_questions: string[];
  cross_references: string[];
  notes?: string;
  source?: 'OpenAI' | 'Perplexity' | 'Hybrid'; // Provider metadata
}

interface AbielGuidanceDisplayProps {
  guidance: AbielGuidance;
  esvReference?: string | null;
  usedESVVerse?: boolean;
}

const AbielGuidanceDisplay: React.FC<AbielGuidanceDisplayProps> = ({
  guidance,
  esvReference,
  usedESVVerse,
}) => {
  // Guidance object is now cleaned by shared utilities during parsing
  // Section checks
  const hasSummary =
    typeof guidance.summary === 'string' && guidance.summary.trim().length > 0;
  const hasReadingGoals = guidance.reading_goals && guidance.reading_goals.length > 0;
  const hasOutline = guidance.how_to_pray && guidance.how_to_pray.outline;
  const hasPrayerPoints =
    Array.isArray(guidance.how_to_pray?.bullet_points) &&
    guidance.how_to_pray!.bullet_points.length > 0;
  const hasPromptsEn = guidance.prompts && Array.isArray(guidance.prompts.en) && guidance.prompts.en.length > 0;
  const hasPromptsZh = guidance.prompts && Array.isArray(guidance.prompts.zh) && guidance.prompts.zh.length > 0;
  const hasJournalingQuestions = guidance.journaling_questions && guidance.journaling_questions.length > 0;
  const hasCrossReferences = guidance.cross_references && guidance.cross_references.length > 0;
  const hasNotes = guidance.notes && guidance.notes.trim().length > 0;

  const hasAnySection =
    hasSummary || hasReadingGoals || hasOutline || hasPrayerPoints ||
    hasPromptsEn || hasPromptsZh || hasJournalingQuestions || hasCrossReferences || hasNotes;

  // ---- Fetch ESV Verses for References ----
  const [verseTexts, setVerseTexts] = useState<Record<string, string>>({});
  const [verseLoading, setVerseLoading] = useState(false);
  const [verseError, setVerseError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchVerses() {
      if (!hasCrossReferences) {
        setVerseTexts({});
        setVerseLoading(false);
        setVerseError(null);
        return;
      }
      setVerseLoading(true);
      setVerseError(null);
      try {
        const results = await Promise.all(
          guidance.cross_references.map(async (ref) => {
            try {
              const verses = await fetchPassage(ref);
              if (Array.isArray(verses) && verses.length > 0) {
                return [ref, verses.map(v => v.text).join(' ')] as [string, string];
              }
              return [ref, 'Verse not found'] as [string, string];
            } catch (err: any) {
              return [ref, 'Failed to fetch'] as [string, string];
            }
          })
        );
        if (!cancelled) {
          setVerseTexts(Object.fromEntries(results));
          setVerseLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setVerseError('Failed to fetch one or more verses.');
          setVerseLoading(false);
        }
      }
    }
    fetchVerses();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidance.cross_references && guidance.cross_references.join('|')]);

  if (!hasAnySection) {
    return (
      <Card>
        <CardContent className="pt-4">
          <div className="text-sm text-muted-foreground">
            No guidance details were provided. Try asking again or rephrasing your request.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 max-w-none">
      {/* Provider Badge */}
      {guidance.source && (
        <div className="flex justify-end">
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
            <span className="mr-1">Powered by</span>
            <span className="font-medium text-primary">{guidance.source}</span>
          </div>
        </div>
      )}
      
      {/* Summary */}
      {hasSummary && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-foreground mb-2">Prayer Guidance</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {stripCodeFences(guidance.summary)}
                </p>
                {usedESVVerse && esvReference && (
                  <div className="mt-2 text-xs text-primary font-medium">
                    📖 Including verse from {esvReference}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reading Goals */}
      {hasReadingGoals && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-2">Reading Goals</h3>
                <ul className="space-y-1">
                  {guidance.reading_goals.map((goal, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prayer Guide Structure */}
      {hasOutline && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-3">Prayer Guide Structure</h3>
                <div className="mb-4 space-y-2">
                  {guidance.how_to_pray.outline.address && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Address:</span> {guidance.how_to_pray.outline.address}
                    </div>
                  )}
                  {guidance.how_to_pray.outline.praise && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Praise:</span> {guidance.how_to_pray.outline.praise}
                    </div>
                  )}
                  {guidance.how_to_pray.outline.confession && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Confession:</span> {guidance.how_to_pray.outline.confession}
                    </div>
                  )}
                  {guidance.how_to_pray.outline.request && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Request:</span> {guidance.how_to_pray.outline.request}
                    </div>
                  )}
                  {guidance.how_to_pray.outline.submission && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Submission:</span> {guidance.how_to_pray.outline.submission}
                    </div>
                  )}
                  {guidance.how_to_pray.outline.thanksgiving && (
                    <div className="text-sm">
                      <span className="font-medium text-primary">Thanksgiving:</span> {guidance.how_to_pray.outline.thanksgiving}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prayer Guidance Points */}
      {hasPrayerPoints && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h4 className="font-medium text-foreground mb-2 text-sm">Prayer Guidance Points:</h4>
                <ul className="space-y-1">
                  {guidance.how_to_pray.bullet_points.map((point, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reflection Prompts */}
      {(hasPromptsEn || hasPromptsZh) && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <MessageCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-2">Reflection Prompts</h3>
                {hasPromptsEn && (
                  <ul className="space-y-1 mb-3">
                    {guidance.prompts.en.map((prompt, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>{prompt}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {hasPromptsZh && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2 text-sm">中文引导：</h4>
                    <ul className="space-y-1">
                      {guidance.prompts.zh!.map((prompt, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>{prompt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journaling Questions */}
      {hasJournalingQuestions && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <PenTool className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-2">Journaling Questions</h3>
                <ul className="space-y-1">
                  {guidance.journaling_questions.map((question, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary">{index + 1}.</span>
                      <span>{question}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cross References (with fetched verse text) */}
      {hasCrossReferences && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Link className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-2">Scripture References</h3>
                <div className="flex flex-col gap-4">
                  {guidance.cross_references.map((reference, index) => (
                    <div key={index} className="mb-2">
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-md font-medium">
                        {reference}
                      </span>
                      <div className="pl-2 mt-1 text-xs text-muted-foreground">
                        {verseLoading
                          ? 'Loading...'
                          : (verseTexts[reference] || 'Verse not found')}
                      </div>
                    </div>
                  ))}
                  {verseError && (
                    <div className="text-xs text-red-500 mt-2">{verseError}</div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes/Guardrails */}
      {hasNotes && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
              <div className="w-full">
                <h3 className="font-medium text-foreground mb-2">Important Notes</h3>
                <p className="text-sm text-muted-foreground">{guidance.notes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AbielGuidanceDisplay;
